import { useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { ProfileCompletionModal } from "@/components/modals/ProfileCompletionModal";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, updateUser, refetchUser } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const showProfileModal = !!user && !user.isProfileCompleted;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying your session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login with return url only if we are not already there
    // The AuthContext generally handles the initial check, but this acts as a guard
    // for protected routes access.
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  return (
    <>
      {children}
      {user && !user.isProfileCompleted ? (
        <ProfileCompletionModal
          user={user}
          isOpen={showProfileModal}
          onCompleted={async (updates) => {
            updateUser({ ...updates, isProfileCompleted: true });
            await refetchUser();
            await queryClient.invalidateQueries();
          }}
        />
      ) : null}
    </>
  );
}
